import {Alert, IconButton, openDialog, Parameters, Separator, Tooltip} from 'platform/components';
import {
  Box,
  Heading,
  Hide,
  HStack,
  Show,
  Space,
  Stack,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {and, or, reverse} from 'ramda';
import {isArray, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useSmsGetKonzultaCredentialsQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {DOT_CHARACTER, suffixTestId} from 'shared/web';

import {useEmailConfig} from '../../hooks/useEmailConfig';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {EntityHeaderControls} from '../EntityHeader/components/EntityHeaderControls';
import {ParameterBlock} from '../EntityHeader/components/ParameterBlock';
import {SkeletonLoadingBox} from '../EntityHeader/components/SkeletonLoadingBox';
import {VehicleConditionEvaluation} from '../EntityHeader/components/VehicleConditionEvaluation';
import type {EntityHeaderProps} from '../EntityHeader/EntityHeader';
import {SendCustomerEmailForm} from '../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../SendCustomerSmsForm/SendCustomerSmsForm';
import {EntityHeaderActions} from './components/EntityHeaderActions';
import {EntityHeaderFlagsV2} from './components/EntityHeaderFlagsV2';
import {EntityHeaderImageV2} from './components/EntityHeaderImageV2';
import {EntityHeaderTitle} from './components/EntityHeaderTitle';
import {ListEditableTagsV2} from './components/ListEditableTagsV2';
import {SnippetPanelV2} from './components/SnippetPanelV2';
import type {EntityHeaderAction, EntityHeaderImageVariant} from './types';

interface EntityHeaderV2Props extends Omit<EntityHeaderProps, 'actions'> {
  actions?: EntityHeaderAction[];
  imageVariant?: EntityHeaderImageVariant;
}

export function EntityHeaderV2(props: EntityHeaderV2Props) {
  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();
  const {data: konzultaConfig} = useSmsGetKonzultaCredentialsQuery();

  const device = useDevice();

  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });

  const hasAnyParameter =
    props.primaryParameter || props.secondaryParameter || props.tertiaryParameter;

  const hasFlags = isNotNilOrEmpty(props.flags);

  const actions = match(device)
    .with('mobile', () => reverse(props.actions ?? []))
    .with('tablet', () => reverse(props.actions ?? []))
    .otherwise(() => props.actions ?? []);

  return (
    <>
      <Hide onNotebook onDesktop>
        <EntityHeaderImageV2
          data-testid={suffixTestId('entityHeader-image', props)}
          icon={props.icon}
          image={props.image}
          onImageClick={props.onImageClick}
          isTitleLoading={props.isEntityLoading}
          ratio={props.imageRatio}
          title={props.title}
          imageVariant={props.imageVariant}
        />
      </Hide>

      <Box padding={[3, 3, 4]} backgroundColor="palettes.white.10.100">
        <VStack>
          <Stack direction={['column', 'column', 'row']} spacing={[3, 3, 4]} align="stretch">
            <Show onNotebook onDesktop>
              <EntityHeaderImageV2
                data-testid={suffixTestId('entityHeader-image', props)}
                icon={props.icon}
                image={props.image}
                onImageClick={props.onImageClick}
                ratio={props.imageRatio}
              />
            </Show>

            <VStack spacing={2}>
              <Show
                when={or(
                  isNotNil(props.flags),
                  and(isNotNil(props.resourceId), isNotNil(props.recordId))
                )}
              >
                <HStack spacing={1} align="center" wrap>
                  <EntityHeaderFlagsV2
                    data-testid={props['data-testid']}
                    dropdownFlags={props.dropdownFlags}
                    flags={props.flags}
                    isLoading={props.isEntityLoading}
                  />

                  <Show when={props.recordId && props.resourceId && hasFlags}>
                    <Text color="tertiary">{DOT_CHARACTER}</Text>
                  </Show>

                  <Show when={props.recordId && props.resourceId}>
                    <ListEditableTagsV2
                      buttonVariant="secondary"
                      data-testid={suffixTestId('entityHeader-editable-tags', props)}
                      isLoading={props.isEntityLoading}
                      isTagAssignable={props.permissions?.tag?.isAssignable}
                      isTagCreatable={props.permissions?.tag?.isCreatable}
                      isTagDeletable={props.permissions?.tag?.isDeletable}
                      isTagReadable={props.permissions?.tag?.isReadable}
                      isTagUnassignable={props.permissions?.tag?.isUnassignable}
                      isTagUpdatable={props.permissions?.tag?.isUpdatable}
                      recordId={props.recordId as string}
                      resourceId={props.resourceId as EntityResourceIds}
                      size="default"
                    />
                  </Show>
                </HStack>
              </Show>

              <Show onMobile onTablet>
                <Separator spacing={1} />
              </Show>

              <Show onNotebook onDesktop>
                <EntityHeaderTitle
                  data-testid={suffixTestId('entityHeader-title', props)}
                  isLoading={props.isEntityLoading}
                  title={props.title}
                />
              </Show>

              <HStack spacing={1} wrap>
                <SkeletonLoadingBox isLoading={props.isEntityLoading} height={5} width={40}>
                  <Parameters
                    color="secondary"
                    data-testid={suffixTestId('entityHeader-parameters', props)}
                    parameters={props.parameters}
                    size="small"
                  />
                </SkeletonLoadingBox>

                <Show when={canReadBusinessCase}>
                  <SkeletonLoadingBox isLoading={props.isEntityLoading} height={5} width={40}>
                    <VehicleConditionEvaluation
                      color="secondary"
                      data-testid={suffixTestId('entityHeader-vehicle-condition', props)}
                      size="small"
                      vehicleCondition={props.vehicleConditionEvaluation}
                    />
                  </SkeletonLoadingBox>
                </Show>
              </HStack>

              <Show onMobile onTablet>
                <Separator spacing={1} />
              </Show>

              <Show when={and(isNotNil(props.recordId), props?.controls?.length)}>
                <EntityHeaderControls
                  authorizationProfileRecordId={props.authorizationProfileRecordId}
                  controls={props.controls!}
                  data-testid={suffixTestId('entityHeader-controls', props)}
                  enableTeams={props.enableTeams}
                  isAuthorizationProfileEditable={props.isAuthorizationProfileEditable}
                  onAuthorizationProfileChange={props.onAuthorizationProfileChange}
                  parkingRecordId={props.parkingRecordId}
                  permissions={props.permissions}
                  recordId={props.recordId as string}
                  resourceId={props.resourceId}
                />
              </Show>

              <Show onMobile onTablet>
                <Separator spacing={0} />
              </Show>
            </VStack>

            <Hide onMobile onTablet>
              <Space fillAvailable />
            </Hide>

            <Show when={props.snippets} onMobile onTablet>
              <Hide onMobile onTablet>
                <Space vertical={4} />
              </Hide>
              <SnippetPanelV2
                areSnippetsLoading={props.isLoadingSnippets}
                data-testid={suffixTestId('entityHeader-snippets', props)}
                resourceId={props.resourceId}
                snippets={props.snippets}
              />
            </Show>

            <Stack
              align="flex-end"
              direction={['column-reverse', 'column-reverse', 'column', 'column']}
              justify="space-between"
              spacing={2}
              width={['100%', '100%', 'auto']}
            >
              <Show when={isNotNilOrEmpty(actions)}>
                <SkeletonLoadingBox isLoading={props.isEntityLoading} width={30} height={8}>
                  <EntityHeaderActions
                    actions={actions}
                    data-testid={suffixTestId('entityHeader-actions', props)}
                    dropdownPosition={['end', 'end', 'start']}
                    maxVisibleActions={[2, 3, 4]}
                    shouldStretchActions
                    wrap={[true, true, false]}
                  />
                </SkeletonLoadingBox>
              </Show>

              <HStack spacing={4}>
                <Show when={props.resourceId === 'VEHICLE' && props.additionalParameterBlocks}>
                  {props.additionalParameterBlocks?.map((block) => (
                    <ParameterBlock
                      isEmailConfigEnabled={isEmailConfigEnabled}
                      key={block.primaryParameter}
                      konzultaConfig={konzultaConfig}
                      primaryParameter={block.primaryParameter}
                      recordId={props.recordId}
                      resourceId={props.resourceId}
                      secondaryParameter={block.secondaryParameter}
                    />
                  ))}
                </Show>

                <Show
                  when={
                    hasAnyParameter &&
                    props.resourceId === 'VEHICLE' &&
                    props.additionalParameterBlocks
                  }
                >
                  <Separator orientation="vertical" />
                </Show>

                <Show when={hasAnyParameter}>
                  <VStack spacing={1} align="flex-end">
                    <Show when={props.primaryParameter}>
                      <HStack spacing={1} align="center">
                        <Heading
                          data-testid={suffixTestId('entityHeader-primaryParameter', props)}
                          isSingleLine
                          size={3}
                        >
                          {props.primaryParameter}
                        </Heading>
                        <Show
                          when={
                            props.resourceId === 'CUSTOMER' &&
                            props.primaryParameter !==
                              i18n.t('entity.customer.labels.noPhoneNumber')
                          }
                        >
                          <Tooltip
                            label={
                              konzultaConfig?.enabled
                                ? i18n.t('general.actions.sendMessage')
                                : i18n.t('general.notifications.setupSmsProvider')
                            }
                            placement="top-end"
                          >
                            <IconButton
                              data-testid="open-send-message-modal"
                              icon="communication/textsms"
                              isDisabled={!konzultaConfig?.enabled}
                              severity="informational"
                              onClick={() =>
                                openDialog(
                                  <SendCustomerSmsForm
                                    customerId={props.recordId}
                                    recordId={props.recordId}
                                    resourceId={props.resourceId}
                                  />,
                                  {
                                    scrollBehavior: 'outside',
                                    size: 'large',
                                    title: i18n.t('general.labels.smsMessage'),
                                  }
                                )
                              }
                            />
                          </Tooltip>
                        </Show>
                        <Show when={isNotNilOrEmpty(props.tertiaryParameter)}>
                          <Heading
                            data-testid={suffixTestId('entityHeader-tertiaryParameter', props)}
                            isSingleLine
                            size={3}
                          >
                            {`/ ${props.tertiaryParameter ?? ''}`}
                          </Heading>
                        </Show>
                      </HStack>
                    </Show>
                    <Show when={props.secondaryParameter}>
                      <HStack spacing={1} align="center">
                        <Text
                          color="secondary"
                          data-testid={suffixTestId('entityHeader-secondaryParameter', props)}
                          noWrap
                          size={['xSmall', 'xSmall', 'base']}
                        >
                          {props.secondaryParameter}
                        </Text>
                        <Show
                          when={
                            props.resourceId === 'CUSTOMER' &&
                            props.secondaryParameter !== i18n.t('entity.customer.labels.noEmail')
                          }
                        >
                          <Tooltip
                            label={
                              isEmailConfigEnabled
                                ? i18n.t('general.actions.sendEmail')
                                : i18n.t('general.notifications.setupEmailProvider')
                            }
                            placement="top-end"
                          >
                            <IconButton
                              data-testid="open-send-email-modal"
                              icon="communication/email"
                              isDisabled={!isEmailConfigEnabled}
                              severity="informational"
                              onClick={() =>
                                openDialog(
                                  <SendCustomerEmailForm
                                    customerId={props.recordId}
                                    recordId={props.recordId}
                                    resourceId={props.resourceId}
                                  />,
                                  {
                                    scrollBehavior: 'outside',
                                    size: 'large',
                                    title: i18n.t('general.actions.sendEmail'),
                                  }
                                )
                              }
                            />
                          </Tooltip>
                        </Show>
                      </HStack>
                    </Show>
                  </VStack>
                </Show>
              </HStack>
            </Stack>
          </Stack>

          <Hide onMobile onTablet>
            <Show when={props.snippets}>
              <Space vertical={4} />
              <SnippetPanelV2
                areSnippetsLoading={props.isLoadingSnippets}
                data-testid={suffixTestId('entityHeader-snippets', props)}
                resourceId={props.resourceId}
                snippets={props.snippets}
              />
            </Show>
          </Hide>
        </VStack>
      </Box>

      <Show when={props.alerts}>
        <VStack spacing={1}>
          {props.alerts?.map((alert, index) => (
            <Alert
              key={isArray(alert.message) ? alert.message?.[0] : alert.message}
              {...alert}
              data-testid={suffixTestId(`entityHeader-alert-[${index}]`, props)}
            />
          ))}
        </VStack>
      </Show>
    </>
  );
}
